/**
 * Read-only view model for Profile screen (legacy `ProfileScreen` field parity, POC subset).
 *
 * Field keys match `BuxusPropertyContractResolver` output from `get-registration-profile`
 * (`data` = `BuxusRegistration` cached in `REGISTRATION_PROFILE_JSON_KEY`).
 */

import {
  asRecord,
  trimmedString,
  trimmedStringArray,
  trimmedStringField,
} from "../util/recordAccess";

export type ProfileRegistrationRow = { label: string; values: string[] };

/** Legacy: stakeholder label often stored with a short prefix; strip first 5 chars like mobile. */
export function formatStakeholderDisplay(stakeholderType: string): string {
  return stakeholderType.length > 5 ? stakeholderType.slice(5) : stakeholderType;
}

function jurisdictionDisplayLines(jur: unknown): string[] {
  if (!Array.isArray(jur) || jur.length === 0) return [];
  const lines: string[] = [];
  for (const j of jur) {
    const jr = asRecord(j);
    if (!jr) continue;
    const dv = trimmedString(jr.displayvalue);
    if (dv) {
      lines.push(dv);
      continue;
    }
    const county = trimmedString(jr.county);
    const state = trimmedString(jr.state);
    if (county && state) lines.push(`${county} - ${state}`);
  }
  return lines;
}

/**
 * Core rows shown on legacy Profile for typical operator / agency users (POC parity with screenshot).
 */
export function profileRegistrationRows(data: unknown): ProfileRegistrationRow[] {
  const reg = asRecord(data);
  if (!reg) return [];

  const rows: ProfileRegistrationRow[] = [];

  const pushOne = (label: string, value: string | undefined) => {
    if (value) rows.push({ label, values: [value] });
  };
  const pushMulti = (label: string, values: string[] | undefined) => {
    if (values?.length) rows.push({ label, values });
  };

  pushOne("Email", trimmedStringField(reg, "email"));
  pushOne("Title", trimmedStringField(reg, "jobRoleTitle"));

  const stakeholderType = trimmedStringField(reg, "stakeholderType");
  if (stakeholderType) pushOne("Stakeholder", formatStakeholderDisplay(stakeholderType));

  pushMulti("Agency Type", trimmedStringArray(reg.agencyType));
  pushOne("Agency City", trimmedStringField(reg, "agencyCity"));
  pushOne("Agency Zip", trimmedStringField(reg, "agencyZip"));
  pushOne("Agency County", trimmedStringField(reg, "county"));
  pushOne("Agency State", trimmedStringField(reg, "state"));

  const jurLines = jurisdictionDisplayLines(reg.jurisdiction);
  if (jurLines.length) rows.push({ label: "Jurisdictions", values: jurLines });

  return rows;
}
